.page {
  width: 100%;
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-6);
  margin-bottom: var(--space-10);
  flex-wrap: wrap;
}

.headerLeft {
  display: flex;
  align-items: flex-start;
  gap: var(--space-6);
  flex: 1;
  min-width: 280px;
}

.headerInfo {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.nameRow {
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.playerName {
  font-family: var(--font-heading);
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 400;
  color: var(--color-text);
  letter-spacing: -0.01em;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--color-text-dim);
}

.status {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.status.online {
  color: var(--color-online);
  font-weight: 700;
}

.headerRight {
  display: flex;
  gap: var(--space-3);
  flex-wrap: wrap;
}

/* Stats Section */
.statsSection {
  margin-bottom: var(--space-12);
}

.sectionTitle {
  font-family: var(--font-heading);
  font-size: 24px;
  font-weight: 400;
  color: var(--color-text);
  margin-bottom: var(--space-6);
  letter-spacing: -0.01em;
}

.statsGrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-4);
}

@media (min-width: 640px) {
  .statsGrid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 1024px) {
  .statsGrid {
    grid-template-columns: repeat(8, 1fr);
  }
}

.statCard {
  text-align: center;
  padding: var(--space-5) var(--space-4);
  transition: all var(--transition-base);
}

.statCard:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
  border-color: var(--color-border-bright);
}

.statHeader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-2);
}

.statIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(0,0,0,0.2);
  border-radius: var(--radius-md);
}

.statLabel {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-dim);
}

.statValue {
  font-family: var(--font-heading);
  font-size: 24px;
  font-weight: 400;
  line-height: 1.2;
}

/* Activity Section */
.activitySection {
  margin-bottom: var(--space-12);
}

.activityPanel {
  min-height: 280px;
}

.chart {
  position: relative;
  height: 240px;
  padding: var(--space-4) var(--space-2) var(--space-8);
}

.chartSvg {
  width: 100%;
  height: 100%;
  display: block;
}

.chartPoint {
  transition: r var(--transition-fast);
}

.chartPoint:hover {
  r: 6;
}

.chartLabels {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  height: 30px;
  font-family: var(--font-body);
  font-size: 10px;
  color: var(--color-text-muted);
}

.chartLabels span {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 100%;
  padding-bottom: var(--space-1);
}

.chartTooltip {
  position: absolute;
  bottom: var(--space-2);
  left: 50%;
  transform: translateX(-50%);
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--color-text-muted);
  white-space: nowrap;
}

/* Breakdown Section */
.breakdownSection {
  margin-bottom: var(--space-8);
}

.breakdownGrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-4);
}

@media (min-width: 768px) {
  .breakdownGrid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.breakdownCard {
  text-align: center;
  padding: var(--space-5) var(--space-4);
}

.breakdownLabel {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-dim);
  margin-bottom: var(--space-2);
}

.breakdownValue {
  font-family: var(--font-heading);
  font-size: 20px;
  font-weight: 400;
  color: var(--color-text);
  margin-bottom: var(--space-3);
}

.breakdownBar {
  height: 6px;
  background: var(--color-bg-input);
  border-radius: var(--radius-pill);
  overflow: hidden;
}

.breakdownFill {
  height: 100%;
  border-radius: var(--radius-pill);
  transition: width var(--transition-slow);
}

/* Loading & Not Found */
.loading,
.notFound {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: var(--space-4);
  color: var(--color-text-dim);
  text-align: center;
  padding: var(--space-8);
}

.spinner {
  width: 40px;
  height: 40px;
  color: var(--color-primary);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.notFound {
  background: var(--color-bg-panel);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  max-width: 400px;
  margin: 0 auto;
  padding: var(--space-12) var(--space-8);
}

.bigIcon {
  width: 80px;
  height: 80px;
  color: var(--color-red);
  opacity: 0.6;
  margin-bottom: var(--space-4);
}

.notFound h1 {
  font-family: var(--font-heading);
  font-size: 28px;
  font-weight: 400;
  color: var(--color-text);
  margin-bottom: var(--space-3);
}

.notFound p {
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--color-text-dim);
  line-height: 1.6;
  margin-bottom: var(--space-6);
}

.notFound strong {
  color: var(--color-text);
}